import { useState } from 'react';
import { Box, Button, Card, ThemeProvider, Typography } from '@mui/material';
import { getTheme } from '@/theme';
import type { ThemeConfig } from '@/api/endpoints/settings';
import { KpiStatCard } from './KpiStatCard';
import { UsageRingCard } from './UsageRingCard';

const FALLBACK: ThemeConfig = { palette: null, style: 'balanced', dark_default: false };

// Named one-click looks. `palette: null` keeps the tenant brand color (white-label).
// "Classic" is the default — switching is an OPT-IN option, fully reversible, and
// never replaces the existing look. Fine-grained palette/style/dark controls live
// in the Console (console.anoorehr.com → Website & Content → Theme); the product
// self-service is intentionally a simple "pick a look".
const PRESETS: { key: string; name: string; desc: string; config: ThemeConfig }[] = [
  { key: 'classic', name: 'Classic', desc: 'Original look', config: { palette: null, style: 'balanced', dark_default: false } },
  { key: 'panel', name: 'AnooreHR Panel', desc: 'Modern flat', config: { palette: null, style: 'minimal', dark_default: false } },
  { key: 'panel_soft', name: 'Panel Soft', desc: 'Rounded & soft', config: { palette: null, style: 'soft', dark_default: false } },
];
const presetMatches = (a: ThemeConfig, b: ThemeConfig) =>
  a.palette === b.palette && a.style === b.style && a.dark_default === b.dark_default;

export interface ThemeSettingsCardProps {
  /** Current resolved/override theme. */
  value?: ThemeConfig | null;
  /** The tenant brand color (so the preview previews correctly). */
  brandColor?: string | null;
  saving?: boolean;
  onSave: (theme: ThemeConfig) => void;
}

/**
 * Tenant self-service theme picker — a simple one-click look selector with a
 * live preview built from the same panel primitives the product renders, so
 * what you see is what you get. Deeper controls live in the Console.
 */
export function ThemeSettingsCard({ value, brandColor, saving, onSave }: ThemeSettingsCardProps) {
  // Start from the current value, but snap to the closest preset's full config so
  // the preview and Save reflect a complete, valid look.
  const [draft, setDraft] = useState<ThemeConfig>({ ...FALLBACK, ...(value ?? {}) });

  const previewTheme = getTheme(
    draft.dark_default ? 'dark' : 'light',
    draft.palette,
    brandColor ?? undefined,
    draft.style,
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
      {/* Controls — just pick a look */}
      <Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', mb: 1 }}>
          CHOOSE A LOOK
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => {
            const selected = presetMatches(draft, p.config);
            return (
              <Box
                key={p.key}
                role="button"
                onClick={() => setDraft({ ...p.config })}
                sx={{
                  flex: '1 1 130px',
                  minWidth: 130,
                  p: 1.25,
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  boxShadow: selected ? (t) => `0 0 0 1px ${t.palette.primary.main}` : 'none',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{p.name}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{p.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 2 }}>
          Looks layer over your platform&rsquo;s default and keep your brand color.
          Switching is reversible — pick Classic any time to revert.
        </Typography>

        <Button variant="contained" onClick={() => onSave(draft)} disabled={saving}>
          {saving ? 'Saving…' : 'Save theme'}
        </Button>
      </Box>

      {/* Live preview */}
      <ThemeProvider theme={previewTheme}>
        <Card
          sx={{
            p: 2,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>
            LIVE PREVIEW
          </Typography>
          <KpiStatCard label="Total Revenue" value="₦42.0M" delta="12.4%" trend="up" accentValue icon={<span>₦</span>} />
          <UsageRingCard
            title="Leave Utilization"
            percent={42}
            usedValue="182"
            usedLabel="Taken"
            totalValue="430"
            totalLabel="Total"
          />
        </Card>
      </ThemeProvider>
    </Box>
  );
}
